<script setup lang="ts">
const NuxtLinkComp = resolveComponent('NuxtLink')

const { data: page } = await useAsyncData('ueberuns-team', () => queryCollection('ueberuns_team').first())
const { data: dozentenPage } = await useAsyncData('ueberuns-dozenten', () => queryCollection('ueberuns_dozenten').first())

const title = page.value?.seo?.title || page.value?.title
const description = page.value?.seo?.description || page.value?.description

useSeoMeta({
  titleTemplate: '',
  title,
  ogTitle: title,
  description,
  ogDescription: description
})
</script>

<template>
  <div v-if="page">
    <UPageHero
      :description="page.description"
      align="left"
    >
      <template #title>
        <MDC
          :value="page.title"
          unwrap="p"
        />
      </template>
    </UPageHero>

    <UPageSection
      v-for="(section, index) in page.sections"
      :key="index"
      :title="section.title"
      :description="section.description"
      :orientation="section.orientation"
      :reverse="section.reverse ?? false"
    >
      <template #features>
        <UPageFeature
          v-for="(feature, fIndex) in section.features"
          :key="fIndex"
          :title="feature.name ?? ''"
          :description="feature.description"
          :icon="feature.icon"
        />
      </template>

      <NuxtImg
        v-if="section.image?.src"
        :src="section.image.src"
        :alt="section.image.alt || section.title"
        class="w-full rounded-lg shadow-xl"
      />
    </UPageSection>

    <UPageSection
      v-if="dozentenPage"
      id="dozenten"
      :description="dozentenPage.description"
    >
      <template #title>
        <MDC
          :value="dozentenPage.title"
          unwrap="p"
        />
      </template>

      <UContainer>
        <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <component
            :is="dozent.to ? NuxtLinkComp : 'div'"
            v-for="dozent in dozentenPage.dozenten"
            :key="dozent.name"
            :to="dozent.to"
            class="group block"
          >
            <UCard
              class="h-full flex flex-col"
              :class="dozent.to ? 'transition-shadow group-hover:ring-2 group-hover:ring-primary' : ''"
            >
              <NuxtImg
                :src="dozent.image"
                :alt="dozent.name"
                class="w-full aspect-square object-cover rounded-lg mb-4"
                sizes="100vw sm:50vw lg:33vw"
              />

              <h3 class="text-lg font-semibold leading-tight">
                {{ dozent.name }}
              </h3>
              <p class="text-sm text-muted mt-1">
                {{ dozent.role }}
              </p>

              <p
                v-if="dozent.description"
                class="text-sm text-default mt-3"
              >
                {{ dozent.description }}
              </p>

              <template
                v-if="dozent.specializations?.length"
                #footer
              >
                <div class="flex flex-wrap gap-1.5">
                  <UBadge
                    v-for="spec in dozent.specializations"
                    :key="spec"
                    variant="subtle"
                    color="neutral"
                    size="sm"
                  >
                    {{ spec }}
                  </UBadge>
                </div>
              </template>
            </UCard>
          </component>
        </div>
      </UContainer>
    </UPageSection>

    <USeparator />

    <UPageCTA
      title="Du unterrichtest gerne und kennst dein Fach?"
      description="Wir sind immer auf der Suche nach erfahrenen Fachpersonen, die ihr Wissen weitergeben möchten. Meld dich, wir freuen uns auf das Gespräch."
      variant="naked"
      :links="[
        { label: 'Als Dozent bewerben', to: '/kontakt', trailingIcon: 'i-lucide-arrow-right' },
        { label: 'Zurück zu Über uns', to: '/ueber-uns', variant: 'subtle', icon: 'i-lucide-arrow-left' }
      ]"
      class="overflow-hidden"
    >
      <LazyStarsBg />
    </UPageCTA>
  </div>
</template>
